import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { Box, Grid, List, ListItem, Typography } from "@mui/material";
import { useEffect } from "react";
import chem from "../../images/chem.svg";

const About = () => {
  const { trackPageView } = useMatomo();

  useEffect(() => {
    trackPageView({
      documentTitle: "About",
      href: "/about",
    });
  }, [trackPageView]);

  return (
    <Grid container>
      {/* Intro Section */}
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              width: { xs: "90%", sm: "70%" },
              pt: { xs: 2, sm: 6 },
              pb: { xs: 1, sm: 2.5 },
              borderBottom: "2px solid #4d82cb",
              margin: "0 auto",
            }}
          >
            <Typography
              sx={{
                color: "#4D82CB",
                fontWeight: 500,
                fontSize: "x-large",
              }}
            >
              MTXPK.org is designed to help clinicians understand the
              pharmacokinetics of high-dose methotrexate, especially with regard
              to delayed clearance.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              width: { xs: "95%", sm: "80%" },
              margin: "0 auto",
              pt: { xs: 1, sm: 2.5 },
              pb: { xs: 1, sm: 2.5 },
              zIndex: -1,
            }}
          >
            <Typography variant="h6" paragraph sx={{ color: "#4D82CB" }}>
              How it Works
            </Typography>
            <Typography variant="body1" paragraph>
              It uses a pharmacokinetic model to display the concentration vs
              time curve for an individual patient given a specific HDMTX dose
              overlaid upon the population-predicted curve for that dose. The
              original model was informed by &gt;60,000 methotrexate
              concentrations and was validated in adults and children with a
              variety of doses, infusion times and ancestries. See Taylor et al.
              in References below.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Highlighted Section */}
      <Grid container sx={{ backgroundColor: "#21549B", color: "white" }}>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            borderRight: { sm: "3px solid", xs: "none" },
          }}
        >
          <Box
            sx={{
              padding: { xs: 1.5, sm: 2.5 },
              width: { xs: "90%", sm: "70%" },
              margin: "0 auto",
              "& img": {
                display: { xs: "none", lg: "block" },
              },
            }}
          >
            <Typography variant="h6" paragraph>
              About
            </Typography>
            <Typography variant="body1" paragraph>
              <Box component="span" sx={{ fontWeight: 500 }}>
                Methotrexate
              </Box>{" "}
              is an anti-folate chemotherapeutic that is used in high doses in
              both hematologic and non-hematologic cancers. Methotrexate and its
              metabolites are renally eliminated. High dose methotrexate can be
              nephrotoxic, resulting in the inability to excrete the drug, and
              increased risk of further toxicities, including hepatotoxicity,
              myelotoxicity, mucositis, neurological toxicity, and death in some
              cases. Patients receiving high dose methotrexate require
              hydration, alkalization of the urine, and leucovorin rescue to
              reduce the risk of toxicity.
            </Typography>
            <img src={chem} style={{ paddingTop: 20 }} alt="" />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              padding: { xs: 1.5, sm: 2.5 },
              width: { xs: "90%", sm: "80%" },
              margin: "0 auto",
            }}
          >
            <Typography variant="body1" paragraph>
              <Box component="span" sx={{ fontWeight: 500 }}>
                Glucarpidase
              </Box>{" "}
              is an FDA- and EMA-approved treatment indicated to reduce toxic
              plasma methotrexate concentrations in patients with delayed
              methotrexate clearance due to impaired renal function. Appropriate
              administration of glucarpidase is challenging due to the ambiguity
              in the labeled indication. A recent consensus guideline was
              published with an algorithm to provide clarity in when to
              administer glucarpidase, yet clinical interpretation of lab
              results that do not directly correspond to the algorithm prove to
              be a limitation of its use. Administration of glucarpidase should
              optimally occur within 48–60 hours from the start of the HDMTX
              infusion, because life-threatening toxicities may not be
              preventable beyond this time point.
            </Typography>
            <Typography variant="body1" paragraph>
              The prediction of which patients need glucarpidase remains a
              challenge, even with the guidelines defining the thresholds in
              methotrexate concentration at certain time points. The challenge
              comes when the sample was taken at an unusual time point, when the
              concentration is very close to the threshold, and when the
              creatinine is rapidly rising. We have received numerous questions
              from clinicians in these situations, who would like to be able to
              predict whether they need to order glucarpidase so that they can
              administer it within the optimal window of time for efficacy.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* References Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            width: { xs: "90%", sm: "86%" },
            margin: "0 auto",
            padding: { xs: 1.5, sm: 2.5 },
            mb: { xs: 4, sm: 6 },
          }}
        >
          <Typography variant="h6" paragraph sx={{ color: "#4D82CB" }}>
            References
          </Typography>
          <Typography variant="body2" paragraph>
            MTXPK.org: A clinical decision support tool evaluating high‐dose
            methotrexate pharmacokinetics to inform post‐infusion care and use
            of glucarpidase. Taylor ZL, Mizuno T, Punt N, Baskaran B, Navarro
            Sainz A, Shuman W, Felicelli N, Vinks AA, Heldrup J, Ramsey LB. Clin
            Pharmacol Ther. 2020 Sep;108(3):635-643. doi: 10.1002/cpt.1957. Epub
            2020 Jul 18. PMID: 32558929.{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://ascpt.onlinelibrary.wiley.com/doi/abs/10.1002/cpt.1957"
            >
              https://ascpt.onlinelibrary.wiley.com/doi/abs/10.1002/cpt.1957
            </a>
          </Typography>
          <Typography variant="body2" paragraph>
            Preventing and Managing Toxicities of High-Dose Methotrexate. Howard
            SC, McCormick J, Pui CH, Buddington RK, Harvey RD. Oncologist. 2016
            Dec;21(12):1471-1482.{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://doi.org/10.1634/theoncologist.2015-0164"
            >
              https://doi.org/10.1634/theoncologist.2015-0164
            </a>
          </Typography>
          <Typography variant="body2" paragraph>
            Voraxaze® (glucarpidase) [package insert]. Brentwood, TN 37027: BTG
            International Inc, 2019.{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://voraxaze.com/getmedia/21552a20-ab2d-4476-a5ac-93e920aecbed/VORAXAZE-PI_August-2019_2-column-format.pdf"
            >
              https://voraxaze.com/getmedia/21552a20-ab2d-4476-a5ac-93e920aecbed/VORAXAZE-PI_August-2019_2-column-format.pdf
            </a>
          </Typography>
          <Typography variant="body2" paragraph>
            Consensus Guideline for Use of Glucarpidase in Patients with
            High-Dose Methotrexate Induced Acute Kidney Injury and Delayed
            Methotrexate Clearance. Ramsey LB, Balis FM, O’Brien MM, Schmiegelow
            K, Pauley JL, Bleyer A, Widemann BC, Askenazi D, Bergeron S, Shirali
            A, Schwartz S, Vinks AA, Heldrup J. Oncologist. 2018
            Jan;23(1):52-61.{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://doi.org/10.1634/theoncologist.2017-0243"
            >
              https://doi.org/10.1634/theoncologist.2017-0243
            </a>
          </Typography>
          <Typography variant="body2" paragraph>
            Taylor ZL, Miller TP, Poweleit EA, et al. Clinical covariates that
            improve the description of high dose methotrexate pharmacokinetics
            in a diverse population to inform MTXPK.org. Clin Transl Sci. 2023;
            16: 2130-2143. PMID: 37503924.{" "}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href="https://doi.org/10.1111/cts.13600"
            >
              https://doi.org/10.1111/cts.13600
            </a>
          </Typography>
          <Typography variant="body2">NCCN Guidelines (5x)</Typography>
          <List>
            <ListItem>
              <Typography variant="body2">
                - NCCN. Clinical Guidelines in Oncology. Acute lymphoblastic
                Leukemia, version 1.2023. Accessed June 28, 2023.{" "}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.nccn.org/professionals/physician_gls/pdf/all.pdf"
                >
                  https://www.nccn.org/professionals/physician_gls/pdf/all.pdf
                </a>
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                - NCCN. Clinical Guidelines in Oncology. B-cell lymphomas,
                version 4.2023. Accessed June 28, 2023.
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.nccn.org/professionals/physician_gls/pdf/b-cell.pdf"
                >
                  https://www.nccn.org/professionals/physician_gls/pdf/b-cell.pdf
                </a>
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                - NCCN. Clinical Guidelines in Oncology. Pediatric acute
                lymphoblastic leukemia, version 2.2023. Accessed June 28, 2023.{" "}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.nccn.org/professionals/physician_gls/pdf/ped_all.pdf"
                >
                  https://www.nccn.org/professionals/physician_gls/pdf/ped_all.pdf
                </a>
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                - NCCN. Clinical Guidelines in Oncology. Pediatric aggressive
                mature B-cell lymphoma, version 1.2023. Accessed June 28, 2023.
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.nccn.org/professionals/physician_gls/pdf/ped_b-cell.pdf"
                >
                  https://www.nccn.org/professionals/physician_gls/pdf/ped_b-cell.pdf
                </a>
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body2">UpToDate</Typography>
          <List>
            <ListItem>
              <Typography variant="body2">
                - LaCasce, A.S. Therapeutic use and toxicity of high-dose
                methotrexate. UpToDate Waltham 2014; Available from:{" "}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://www.uptodate.com/contents/therapeutic-use-and-toxicity-of-high-dose-methotrexate"
                >
                  https://www.uptodate.com/contents/therapeutic-use-and-toxicity-of-high-dose-methotrexate
                </a>
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body2">European Consensus Guideline</Typography>
          <List>
            <ListItem>
              <Typography variant="body2">
                - Bielack, S.S., Soussain, C., Fox, C.P. et al. A European
                consensus recommendation on the management of delayed
                methotrexate elimination: supportive measures, leucovorin rescue
                and glucarpidase treatment. J Cancer Res Clin Oncol 150, 441
                (2024).
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://doi.org/10.1007/s00432-024-05945-6"
                >
                  https://doi.org/10.1007/s00432-024-05945-6
                </a>
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body2">Who's Citing Us:</Typography>
          <List>
            <ListItem>
              <Typography variant="body2">
                -{" "}
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://pubmed.ncbi.nlm.nih.gov/?linkname=pubmed_pubmed_citedin&from_uid=32558929"
                >
                  https://pubmed.ncbi.nlm.nih.gov/?linkname=pubmed_pubmed_citedin&from_uid=32558929
                </a>
              </Typography>
            </ListItem>
          </List>
        </Box>
      </Grid>
    </Grid>
  );
};

export default About;
